import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import ModalBase from "../../../../components/ui/ModalBase";
import axios from "axios";
import { SERVICEURL } from "../../../../appconfig";
import {
  getEducationDetail,
  updateEducationInfo,
} from "../../../../api/educationApi";
import { useDispatch, useSelector } from "react-redux";
import {
  getCountryList,
  getGraduationStatusList,
  getCollageList,
  getDepartmentList,
} from "../../../../redux/actions/memberAction";
import { useToastContext } from "../../../../context/ToastContextProvider";
import colors from "../../../../shared/constants/color";

export interface AddEducationPayload {
  MemberID: string;
  CollageParameterID?: string | null;
  CountryParameterID?: string | null;
  DepartmentParameterID?: string | null;
  GraduationStatusParameterID?: string | null;
  SchoolName?: string | null;
  DepartmentName?: string | null;
  RegisteredYear?: string | null;
  GraduationYear?: string | null;
  Degree?: string | null;
  IsContinue: boolean;
  IsCertificate: boolean;
  IsItPersonnel: boolean;
}

export interface EducationInfoListDTO {
  educationID: number;
  memberID: number;
  graduationStatusName: string;
  collageParameterName: string;
  departmentParameterName: string;
  registeredYear: string;
  graduationYear: string;
  degree: string;
  isItPersonnel: string;
  isCertificate: string;
}

interface GraduationStatusOption {
  id: string;
  name: string;
}

interface AddEducationModalProps {
  open: boolean;
  memberId: string;
  educationId?: string | null;
  onClose: () => void;
  onSuccess?: (item: EducationInfoListDTO) => void;
}

const initialFormState = {
  graduationStatusParameterID: "" as string | number,
  countryParameterID: "" as string | number,
  collageParameterID: "" as string | number,
  departmentParameterID: "" as string | number,
  schoolName: "",
  departmentName: "",
  status: "",
  registeredYear: "",
  graduationYear: "",
  degree: "",
  isItPersonnel: false,
  isCertificate: false,
  isContinue: false,
};

const AddEducationModal: React.FC<AddEducationModalProps> = ({
  open,
  memberId,
  educationId = null,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!educationId;
  const dispatch = useDispatch();
  const { showToast } = useToastContext();
  const graduationStatusListFromStore = useSelector(
    (state: any) =>
      state.graduationStatusList?.graduationStatusList || []
  ) as GraduationStatusOption[];
  const countryListFromStore = useSelector(
    (state: any) => state.countryList?.countryList || []
  ) as GraduationStatusOption[];
  const collageListFromStore = useSelector(
    (state: any) => state.collageList?.collageList || []
  ) as GraduationStatusOption[];
  const departmentListFromStore = useSelector(
    (state: any) => state.departmentList?.departmentList || []
  ) as GraduationStatusOption[];
  const [form, setForm] = useState<typeof initialFormState>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [graduationStatuses, setGraduationStatuses] = useState<
    GraduationStatusOption[]
  >([]);
  const selectedCountry =
    countryListFromStore.find(
      (c) => String(c.id) === String(form.countryParameterID)
    ) || null;
  const selectedGraduationStatus =
    graduationStatuses.find(
      (s) => String(s.id) === String(form.graduationStatusParameterID)
    ) || null;
  const selectedCollage =
    collageListFromStore.find(
      (c) => String(c.id) === String(form.collageParameterID)
    ) || null;
  const selectedDepartment =
    departmentListFromStore.find(
      (d) => String(d.id) === String(form.departmentParameterID)
    ) || null;

  const statusOptions = [
    { id: "continue", name: "Devam Ediyor" },
    { id: "graduate", name: "Mezun" },
  ];
  const selectedStatus =
    statusOptions.find((s) => s.id === form.status) || null;

  useEffect(() => {
    if (!open) return;

    dispatch(getGraduationStatusList());
    dispatch(getCountryList());
    dispatch(getCollageList());
    dispatch(getDepartmentList());

    // Düzenleme modunda veriyi yükle
    if (educationId) {
      loadEducationData(educationId);
    } else {
      resetState();
    }
  }, [open, dispatch, educationId]);

  const loadEducationData = async (id: string) => {
    try {
      const data = await getEducationDetail(id);
      console.log("Loaded education data:", data);
      
      // Status belirleme
      let status = "";
      if (data.isContinue) {
        status = "continue";
      } else if (data.graduationYear && data.graduationYear > 0) {
        status = "graduate";
      }

      // Degree formatla
      let degreeStr = "";
      if (data.degree && data.degree > 0) {
        // API'den gelen değer 3 ise -> "3", 300 ise -> "3.00", 285 ise -> "2.85"
        if (data.degree >= 100) {
          // 300 -> "3.00", 285 -> "2.85"
          const degreeValue = (data.degree / 100).toFixed(2);
          degreeStr = degreeValue;
        } else if (data.degree < 10) {
          // 3 -> "3.00"
          degreeStr = data.degree.toFixed(2);
        } else {
          // Diğer durumlar için olduğu gibi kullan
          degreeStr = data.degree.toString();
        }
      }

      setForm({
        graduationStatusParameterID: data.graduationStatusParameterId || "",
        countryParameterID: data.countryParameterId || "",
        collageParameterID: data.collageParameterId || "",
        departmentParameterID: data.departmentParameterId || "",
        schoolName: data.schoolName || "",
        departmentName: data.departmentName || "",
        status: status,
        registeredYear: data.registeredYear?.toString() || "",
        graduationYear: data.graduationYear && data.graduationYear > 0 ? data.graduationYear.toString() : "",
        degree: degreeStr,
        isItPersonnel: data.isItPersonnel || false,
        isCertificate: data.isCertificate || false,
        isContinue: data.isContinue || false,
      });

      console.log("Form set with:", {
        graduationStatusParameterID: data.graduationStatusParameterId,
        collageParameterID: data.collageParameterId,
        departmentParameterID: data.departmentParameterId,
        status: status,
        degree: degreeStr,
      });
    } catch (error) {
      console.error("Error loading education data:", error);
      showToast("Eğitim bilgisi yüklenirken bir hata oluştu.", "error");
    }
  };

  useEffect(() => {
    setGraduationStatuses(graduationStatusListFromStore || []);
  }, [graduationStatusListFromStore]);

  useEffect(() => {
    const graduationStatusId = Number(form.graduationStatusParameterID);
    const requiresDepartment = [1, 2, 3].includes(graduationStatusId);

    if (requiresDepartment && !form.departmentParameterID) {
      setErrors((prev) => ({
        ...prev,
        departmentParameterID: "Bölüm seçimi zorunludur.",
      }));
    } else if (!requiresDepartment || form.departmentParameterID) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.departmentParameterID;
        return newErrors;
      });
    }
  }, [form.graduationStatusParameterID, form.departmentParameterID]);


  const resetState = () => {
    setForm(initialFormState);
    setErrors({});
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "" || /^[0-9]{0,4}$/.test(value)) {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDegreeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    const isNumber = /^[0-9]$/.test(e.key);
    const isAllowedKey = allowedKeys.includes(e.key);

    if (!isNumber && !isAllowedKey) {
      e.preventDefault();
    }
  };

  const handleDegreeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;

    if (value === "") {
      setForm((prev) => ({ ...prev, degree: "" }));
      return;
    }

    value = value.replace(/[^0-9]/g, "");
    if (value.length > 3) {
      value = value.substring(0, 3);
    }

    if (value.length === 1) {
      setForm((prev) => ({ ...prev, degree: value }));
      return;
    }

    const formatted = `${value.charAt(0)}.${value.substring(1)}`;
    setForm((prev) => ({ ...prev, degree: formatted }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleStatusChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setForm((prev) => ({
      ...prev,
      status: value,
      isContinue: value === "continue",
      graduationYear: value === "graduate" ? prev.graduationYear : "",
    }));
    
    // Status seçildiğinde hatasını temizle
    if (value) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.status;
        return newErrors;
      });
    }
  };

  // Zorunlu alanları kontrol eden yardımcı fonksiyon
  const validateField = (fieldName: string, value: any) => {
    const currentYear = new Date().getFullYear();
    
    switch (fieldName) {
      case "graduationStatusParameterID":
        if (!value) {
          setErrors((prev) => ({ ...prev, graduationStatusParameterID: "Mezuniyet durumu zorunludur." }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.graduationStatusParameterID;
            return newErrors;
          });
        }
        break;
      
      case "collageParameterID":
        if (!value && !form.schoolName?.trim()) {
          setErrors((prev) => ({ ...prev, collageParameterID: "Okul seçimi zorunludur." }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.collageParameterID;
            return newErrors;
          });
        }
        break;
      
      case "status":
        if (!value) {
          setErrors((prev) => ({ ...prev, status: "Durum seçimi zorunludur." }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.status;
            return newErrors;
          });
        }
        break;
      
      case "registeredYear":
        if (!value) {
          setErrors((prev) => ({ ...prev, registeredYear: "Giriş yılı zorunludur." }));
        } else {
          const year = parseInt(value, 10);
          if (isNaN(year) || year < 1900 || year > currentYear) {
            setErrors((prev) => ({ ...prev, registeredYear: `Giriş yılı 1900 ile ${currentYear} arasında olmalıdır.` }));
          } else {
            setErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors.registeredYear;
              return newErrors;
            });
          }
        }
        break;
      
      case "graduationYear":
        if (form.status === "graduate" && !value) {
          setErrors((prev) => ({ ...prev, graduationYear: "Mezuniyet yılı zorunludur." }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.graduationYear;
            return newErrors;
          });
        }
        break;
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const currentYear = new Date().getFullYear();

    if (!form.graduationStatusParameterID) {
      newErrors.graduationStatusParameterID = "Mezuniyet durumu zorunludur.";
    }
    if (!form.collageParameterID && !form.schoolName?.trim()) {
      newErrors.collageParameterID = "Okul seçimi zorunludur.";
    }
    if (!form.status) {
      newErrors.status = "Durum seçimi zorunludur.";
    }
    if (!form.registeredYear) {
      newErrors.registeredYear = "Giriş yılı zorunludur.";
    } else {
      const year = parseInt(form.registeredYear, 10);
      if (isNaN(year) || year < 1900 || year > currentYear) {
        newErrors.registeredYear = `Giriş yılı 1900 ile ${currentYear} arasında olmalıdır.`;
      }
    }
    if (form.status === "graduate" && !form.graduationYear) {
      newErrors.graduationYear = "Mezuniyet yılı zorunludur.";
    }

    const graduationStatusId = Number(form.graduationStatusParameterID);
    const requiresDepartment = [1, 2, 3].includes(graduationStatusId);

    if (requiresDepartment && !form.departmentParameterID) {
      newErrors.departmentParameterID = "Bölüm seçimi zorunludur.";
    }

    if (form.degree) {
      const degreeValue = parseFloat(form.degree.replace(",", "."));
      if (isNaN(degreeValue) || degreeValue < 1.8 || degreeValue > 5.0) {
        newErrors.degree =
          "Mezuniyet derecesi 1.8 ile 5.00 arasında olmalıdır.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    
    try {
      setIsSubmitting(true);

      if (isEditMode && educationId) {
        // Düzenleme modu - PUT isteği
        // Zorunlu alanlar kontrol edildi (validate fonksiyonunda)
        
        // Degree'yi API formatına çevir
        let degreeForApi = 0;
        if (form.degree && !form.isContinue) {
          const degreeValue = parseFloat(form.degree.replace(",", "."));
          if (!isNaN(degreeValue)) {
            if (degreeValue < 10) {
              degreeForApi = degreeValue;
            } else {
              degreeForApi = degreeValue;
            }
          }
        }

        const updatePayload: any = {
          educationId: educationId,
          collageParameterId: form.collageParameterID || undefined,
          graduationStatusParameterId: form.graduationStatusParameterID || undefined,
          registeredYear: Number(form.registeredYear),
          schoolName: form.schoolName || "",
          departmentName: form.departmentName || "",
          graduationYear: form.isContinue ? 0 : (form.graduationYear ? Number(form.graduationYear) : 0),
          degree: degreeForApi,
          isContinue: form.isContinue,
          isCertificate: form.isCertificate,
          isItPersonnel: form.isItPersonnel,
        };

        if (form.countryParameterID) {
          updatePayload.countryParameterId = String(form.countryParameterID);
        }
        if (form.departmentParameterID) {
          updatePayload.departmentParameterId = String(form.departmentParameterID);
        }

        console.log("Update payload:", updatePayload);
        await updateEducationInfo(updatePayload);
        showToast("Eğitim bilgisi başarıyla güncellendi.", "success");
        if (onSuccess) {
          onSuccess({} as EducationInfoListDTO);
        }
        handleClose();
      } else {
        // Ekleme modu - POST isteği
        const token = localStorage.getItem("token");
        const payload: AddEducationPayload = {
          MemberID: memberId,
          CollageParameterID:
            form.collageParameterID === "" ? null : String(form.collageParameterID),
          CountryParameterID:
            form.countryParameterID === "" ? null : String(form.countryParameterID),
          DepartmentParameterID:
            form.departmentParameterID === ""
              ? null
              : String(form.departmentParameterID),
          GraduationStatusParameterID:
            form.graduationStatusParameterID === ""
              ? null
              : String(form.graduationStatusParameterID),
          SchoolName: form.schoolName || null,
          DepartmentName: form.departmentName || null,
          RegisteredYear: form.registeredYear || null,
          GraduationYear: form.isContinue ? null : form.graduationYear || null,
          Degree: form.isContinue ? null : form.degree || null,
          IsContinue: form.isContinue,
          IsCertificate: form.isCertificate,
          IsItPersonnel: form.isItPersonnel,
        };

        const response = await axios.post(
          `${SERVICEURL}api/Candidates/AddEducationInfo`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const apiData = response.data?.data as EducationInfoListDTO | undefined;
        if (apiData) {
          if (onSuccess) {
            onSuccess(apiData);
          }
          showToast("Eğitim bilgisi başarıyla eklendi.", "success");
          handleClose();
        } else {
          showToast("Eğitim bilgisi eklenirken bir hata oluştu.", "error");
        }
      }
    } catch (error) {
      console.error("Education save error:", error);
      const errorMessage = isEditMode
        ? "Eğitim bilgisi güncellenirken bir hata oluştu."
        : "Eğitim bilgisi eklenirken bir hata oluştu.";
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ModalBase
        open={open}
        onClose={handleClose}
        title={isEditMode ? "Eğitim Bilgisi Düzenle" : "Eğitim Bilgisi Ekle"}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ borderRadius: 2, textTransform: "none", minWidth: 80 }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            sx={{ borderRadius: 2, minWidth: 80 }}
            disabled={isSubmitting}
            onClick={handleSaveClick}
          >
            {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </>
      }
      maxWidth="md"
      minWidth={600}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
            width: "100%",
          }}
        >
          <Autocomplete
            fullWidth
            options={graduationStatuses || []}
            value={selectedGraduationStatus}
            onChange={(_, newValue) => {
              const value = newValue ? newValue.id : "";
              setForm((prev) => ({
                ...prev,
                graduationStatusParameterID: value,
              }));
              validateField("graduationStatusParameterID", value);
            }}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mezuniyet*"
                error={!!errors.graduationStatusParameterID}
                helperText={errors.graduationStatusParameterID}
                size="small"
              />
            )}
          />
          <Autocomplete
            fullWidth
            options={countryListFromStore || []}
            value={selectedCountry}
            onChange={(_, newValue) => {
              setForm((prev) => ({
                ...prev,
                countryParameterID: newValue ? newValue.id : "",
              }));
            }}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ülke"
                error={!!errors.countryParameterID}
                helperText={errors.countryParameterID}
                size="small"
              />
            )}
          />
          <Autocomplete
            fullWidth
            options={collageListFromStore || []}
            value={selectedCollage}
            onChange={(_, newValue) => {
              const value = newValue ? newValue.id : "";
              setForm((prev) => ({
                ...prev,
                collageParameterID: value,
              }));
              validateField("collageParameterID", value);
            }}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Okul*"
                error={!!errors.collageParameterID}
                helperText={errors.collageParameterID}
                size="small"
              />
            )}
          />
        </Box>

        <Autocomplete
          fullWidth
          options={departmentListFromStore || []}
          value={selectedDepartment}
          onChange={(_, newValue) => {
            setForm((prev) => ({
              ...prev,
              departmentParameterID: newValue ? newValue.id : "",
            }));
          }}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disableClearable={false}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Bölüm"
              size="small"
              error={!!errors.departmentParameterID}
              helperText={errors.departmentParameterID}
            />
          )}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 2,
            width: "100%",
          }}
        >
          <Autocomplete
            fullWidth
            options={statusOptions}
            value={selectedStatus}
            onChange={(_, newValue) => {
              const statusValue = newValue ? newValue.id : "";
              setForm((prev) => ({
                ...prev,
                status: statusValue,
                isContinue: statusValue === "continue",
                graduationYear: statusValue === "graduate" ? prev.graduationYear : "",
              }));
              validateField("status", statusValue);
            }}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Durumu*"
                error={!!errors.status}
                helperText={errors.status}
                size="small"
              />
            )}
          />
          <TextField
            fullWidth
            label="Giriş Yılı*"
            name="registeredYear"
            value={form.registeredYear}
            onChange={handleYearChange}
            onBlur={() => validateField("registeredYear", form.registeredYear)}
            error={!!errors.registeredYear}
            helperText={errors.registeredYear}
            size="small"
            InputProps={{
              endAdornment:
                form.registeredYear !== "" ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: colors.neutral.N1200 },
                      }}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          registeredYear: "",
                        }))
                      }
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
          <TextField
            fullWidth
            label="Mezuniyet Yılı*"
            name="graduationYear"
            value={form.graduationYear}
            onChange={handleYearChange}
            onBlur={() => validateField("graduationYear", form.graduationYear)}
            error={!!errors.graduationYear}
            helperText={errors.graduationYear}
            size="small"
            disabled={form.status !== "graduate"}
            InputProps={{
              endAdornment:
                form.graduationYear !== "" ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: colors.neutral.N1200 },
                      }}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          graduationYear: "",
                        }))
                      }
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
          <TextField
            fullWidth
            label="Derece"
            name="degree"
            value={form.degree}
            onChange={handleDegreeChange}
            onKeyDown={handleDegreeKeyDown}
            size="small"
            error={!!errors.degree}
            helperText={errors.degree}
            InputProps={{
              endAdornment:
                form.degree !== "" ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: colors.neutral.N1200 },
                      }}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          degree: "",
                        }))
                      }
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 4, mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isItPersonnel}
                onChange={handleCheckboxChange}
                name="isItPersonnel"
              />
            }
            label={<Typography sx={{ fontSize: 14 }}>Bilişim Çalışanı</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isCertificate}
                onChange={handleCheckboxChange}
                name="isCertificate"
              />
            }
            label={<Typography sx={{ fontSize: 14 }}>Arge Çalışanı</Typography>}
          />
        </Box>
      </Box>
    </ModalBase>

    <Dialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      disableScrollLock
    >
      <DialogTitle>
        {isEditMode ? "Eğitim Bilgisi Güncellensin mi?" : "Eğitim Bilgisi Eklensin mi?"}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {isEditMode 
            ? "Eğitim bilgisini güncellemek istediğinize emin misiniz?" 
            : "Eğitim bilgisini eklemek istediğinize emin misiniz?"}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
          Hayır
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          sx={{ textTransform: "none", background: colors.blue.B800 }}
        >
          Evet
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddEducationModal;


